import {
  defaultState as defaultAnalyticsState,
  State as AnalyticsState,
} from '@app/features/analytics/domain';
import {
  AuthenticationsState,
  BankQuestionsState,
  CampaignsState,
  ChannelsState,
  CustomActivitiesState,
  CustomActivityFieldsState,
  CustomActivityQuestionsState,
  EventsState,
  FoldersState,
  HubsState,
  LeadFieldsState,
  ProgramMemberFieldsRegState,
  ProgramMemberFieldsState,
  ProgramsState,
  SalesforceCustomActivityFieldsDataState,
  SalesforceEcaMigrationState,
  SalesforceUIState,
  SObjectFields,
  SObjectFieldsState,
  SObjectsState,
  SolutionInstanceState,
  SupportedApp,
  UIState,
} from '@app/routes/apps/domain';
import {
  AttendeeHomeState,
  defaultAttendeeHomeState,
} from '@app/routes/attendee/domain';
import {
  defaultState as defaultAttendeeProfileState,
  State as AttendeeProfileState,
} from '@app/routes/attendee/routes/profile/domain';
import {
  ConnectionsListState,
  defaultConnectionsState,
} from '@app/routes/connections/domain';
import {
  defaultState as defaultContentHubState,
  State as ContentHubState,
} from '@app/routes/content/domain';
import {
  defaultState as defaultEventWizardState,
  State as EventWizardState,
} from '@app/routes/create-event-wizard/domain';
import {
  defaultState as defaultCreateOrganizationState,
  State as CreateOrganizationState,
} from '@app/routes/create-organization/domain';
import {
  defaultState as defaultOrganizationState,
  State as OrganizationState,
} from '@app/routes/organization/domain';
import {
  defaultState as defaultBillingState,
  State as BillingState,
} from '@app/routes/organization/routes/manage/routes/billing/domain';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';

import { TransactionState } from './domain';

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarImage: string;
  largeAvatarImage: string;
  hasAcceptedTerms: boolean;
}

export interface Organization {
  id: string;
  name: string;
  plan?: string;
  planNameLocale?: string;
  image: string;
  smallImage: string;
  largeImage: string;
  createdAt: string;
  features: {
    appStore: boolean;
  };
}

export interface VendorBooth {
  id: string;
  name: string;
  eventName: string;
  image: string;
}

export interface OrganizationInvitation {
  id: string;
  organizationName: string;
  inviterName: string;
}

export type State = {
  initialized: boolean;
  organizationExternalId: string | undefined;
  user: {
    id: string | undefined;
    email: string | undefined;
    authToken: string | undefined;
  };
  featureFlags?: LDFlagSet;
  navBar: {
    isOrgSelected: boolean;
    selectedOrgInfo: {
      name: string;
      image: string;
      paymentPlan: string;
      planNameLocale: string;
      largeImage: string;
    };
    userProfile: {
      data: UserProfile | null;
      error: string | null;
    } & TransactionState;
    organizations: {
      data: Organization[] | null;
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string;
      };
      error: string | null;
      fetched: boolean;
    } & TransactionState;
    vendorBooths: {
      data: VendorBooth[] | null;
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string;
      };
      error: string | null;
      fetched: boolean;
    } & TransactionState;
    isLanguagePickerOpen: boolean;
    isHelpMenuOpen: boolean;
    isOrgSwitcherOpen: boolean;
  };
  apps: {
    editingAppsConfiguration?: SupportedApp;
    events: EventsState;
    hubs: HubsState;
    authentications: AuthenticationsState;
    solutionInstance: SolutionInstanceState;
    bankQuestions: BankQuestionsState;
    marketoData: {
      programs: ProgramsState;
      folders: FoldersState;
      channels: ChannelsState;
      programMemberFields: ProgramMemberFieldsState;
      customActivities: CustomActivitiesState;
      programMemberFieldsReg: ProgramMemberFieldsRegState;
      customActivityQuestions: CustomActivityQuestionsState;
      leadFields: LeadFieldsState;
      customActivityFields: CustomActivityFieldsState;
      uiState: UIState;
    };
    salesforceData: {
      customActivityFields: SalesforceCustomActivityFieldsDataState;
      sobjects: SObjectsState;
      sobjectFields: SObjectFieldsState;
      campaigns: CampaignsState;
      uiState: SalesforceUIState;
      ecaMigration: SalesforceEcaMigrationState;
    };
  };
  contentHub: ContentHubState;
  eventWizard: EventWizardState;
  createOrganization: CreateOrganizationState;
  analytics: AnalyticsState;
  organization: OrganizationState;
  attendeeProfile: AttendeeProfileState;
  attendeeHome: AttendeeHomeState;
  connections: ConnectionsListState;
  billing: BillingState;
  fileDownloadUrls: Record<
    string,
    {
      url: string | null;
      expiresAt: number | null;
      loading: boolean;
      error: string | null;
    }
  >;
};

export const defaultSalesforceEcaMigrationState: SalesforceEcaMigrationState = {
  initialized: false,
  loading: false,
  migration: null,
  finalizationPhase: 'idle',
  errorKey: null,
  warningKey: null,
  previousAuthenticationId: null,
  expectedAuthenticationId: null,
  selectedAuthenticationId: null,
  contextRefreshGeneration: 0,
  contextRefreshPending: [],
  contextRefreshFailed: false,
  connectorRefreshGeneration: 0,
  connectorRefreshPending: [],
  connectorRefreshFailed: false,
};

export const defaultState: State = {
  initialized: false,
  organizationExternalId: undefined,
  user: {
    id: undefined,
    email: undefined,
    authToken: undefined,
  },
  navBar: {
    isOrgSelected: false,
    selectedOrgInfo: {
      name: '',
      image: '',
      paymentPlan: '',
      planNameLocale: '',
      largeImage: '',
    },
    userProfile: {
      data: null,
      status: 'idle',
      error: null,
    },
    organizations: {
      data: null,
      pageInfo: {
        hasNextPage: false,
        endCursor: '',
      },
      status: 'idle',
      error: null,
      fetched: false,
    },
    vendorBooths: {
      data: null,
      pageInfo: {
        hasNextPage: false,
        endCursor: '',
      },
      status: 'idle',
      error: null,
      fetched: false,
    },
    isLanguagePickerOpen: false,
    isHelpMenuOpen: false,
    isOrgSwitcherOpen: false,
  },
  apps: {
    editingAppsConfiguration: undefined,
    events: {
      items: [],
      loading: false,
      error: null,
    },
    hubs: {
      items: [],
      loading: false,
      error: null,
    },
    authentications: {
      items: [],
      loading: false,
      error: null,
    },
    solutionInstance: {
      data: null,
      originalData: null,
      loading: false,
      error: null,
    },
    bankQuestions: {
      items: [],
      loading: false,
      error: null,
    },
    marketoData: {
      programs: {
        items: [],
        loading: false,
        error: null,
      },
      folders: {
        items: [],
        loading: false,
        error: null,
      },
      channels: {
        items: [],
        loading: false,
        error: null,
      },
      programMemberFields: {
        items: [],
        loading: false,
        error: null,
      },
      customActivities: {
        items: [],
        loading: false,
        error: null,
      },
      programMemberFieldsReg: {
        items: [],
        loading: false,
        error: null,
      },
      customActivityQuestions: {
        data: {},
        loading: false,
        error: null,
      },
      leadFields: {
        items: [],
        loading: false,
        error: null,
      },
      customActivityFields: {
        external_attended_event_activity_fields: {
          items: [],
          loading: false,
          error: null,
        },
        external_visited_replay_area_activity_fields: {
          items: [],
          loading: false,
          error: null,
        },
        external_watched_replay_video_activity_fields: {
          items: [],
          loading: false,
          error: null,
        },
      },
      uiState: {
        //Registrations tab
        customActivity: false,
        leadOrProgramFields: false,
        utmParameters: false,
        onsiteActivities: false,
        //Attendance tab
        exportAttendance: false,
        //Engagement data tab
        attendeeActivity: false,
        visitedReplayAreaActivity: false,
        watchedReplayVideoActivity: false,
        // ContentHub Engagement data tab
        contentHubEventAreaActivity: false,
        errorCount: {
          registrations: 0,
          programMapping: 0,
          contentHubProgramMapping: 0,
        },
      },
    },
    salesforceData: {
      sobjects: {
        items: [],
        loading: false,
        error: null,
      },
      sobjectFields: {
        data: {},
        loadingStates: {},
        errors: {},
      },
      campaigns: {
        items: [],
        loading: false,
        error: null,
      },
      customActivityFields: {
        data: {} as Record<string, SObjectFields[]>,
        external_salesforce_attendee_event_area_activity_fields: {
          loading: false,
          error: null,
        },
        external_salesforce_visited_replay_area_activity_fields: {
          loading: false,
          error: null,
        },
        external_salesforce_watched_replay_video_activity_fields: {
          loading: false,
          error: null,
        },
      },
      uiState: {
        engagementData: true,
        engagementDataSetupMethod: 'automatic',
        attendeeActivity: false,
        visitedReplayAreaActivity: false,
        watchedReplayVideoActivity: false,
        attendeeActivityObject: null,
        visitedReplayAreaObject: null,
        watchedReplayVideoObject: null,
        errorCount: {
          engagementData: 0,
          eventMapping: 0,
        },
      },
      ecaMigration: defaultSalesforceEcaMigrationState,
    },
  },
  contentHub: { ...defaultContentHubState },
  eventWizard: { ...defaultEventWizardState },
  createOrganization: { ...defaultCreateOrganizationState },
  analytics: { ...defaultAnalyticsState },
  organization: { ...defaultOrganizationState },
  attendeeProfile: { ...defaultAttendeeProfileState },
  attendeeHome: { ...defaultAttendeeHomeState },
  connections: { ...defaultConnectionsState },
  billing: { ...defaultBillingState },
  fileDownloadUrls: {},
};
